import React from 'react';
import { Button, DatePicker, Input, Radio } from 'antd';
import dayjs from 'dayjs';
import type { MedicareManager, MedicareManagementDto } from '../types';
import { managerSsnValid } from '../validators';
import s from '../common.module.less';

interface Props {
    value?: MedicareManagementDto | null;
    onChange: (value: MedicareManagementDto) => void;
}

const SSN_ERROR = 'Enter a 9-digit social security number.';

// 原型 medicareNotEnrolledHTML 的 managing employee（10/15）：Medicare 筛查日常管理 practice 的人，
// 855I 第 6 节要姓名（分开填，和 SSA 记录比对）、生日、SSN。Yes/No 不预选——默认 No 等于替人答了。
// 数据每个 provider 一份、不按州，所以无论在哪个州的 Medicare 卡里改，都是同一份。
export default function MedicareManagerFields({ value, onChange }: Props) {
    const hasManager = value?.hasManager;
    const managers: MedicareManager[] = value?.managers?.length ? value.managers : [{}];

    const setManagers = (next: MedicareManager[]) => onChange({ ...value, hasManager: true, managers: next });
    const patchAt = (i: number, patch: Partial<MedicareManager>) =>
        setManagers(managers.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));

    let radioValue: 'Yes' | 'No' | undefined;
    if (hasManager === true) radioValue = 'Yes';
    else if (hasManager === false) radioValue = 'No';

    return (
        <div className={s.managerBlock}>
            <span className={s.fieldLabel}>Does anyone else manage this practice day to day?</span>
            <p className={s.managerHint}>
                Someone who runs the practice day to day, an office manager for example. Medicare screens them.
            </p>
            <Radio.Group
                value={radioValue}
                onChange={(e) =>
                    onChange(
                        e.target.value === 'Yes'
                            ? { ...value, hasManager: true, managers }
                            : { ...value, hasManager: false, managers: [] },
                    )
                }
            >
                <Radio value="Yes">Yes</Radio>
                <Radio value="No">No</Radio>
            </Radio.Group>
            {hasManager === true && (
                <>
                    {managers.map((m, i) => {
                        const ssnTouched = !!(m.ssn || '').trim();
                        return (
                            // eslint-disable-next-line react/no-array-index-key
                            <div className={s.managerCard} key={m.id || `new-${i}`}>
                                <div className={s.managerCardHeader}>
                                    <span>Managing employee {i + 1}</span>
                                    {managers.length > 1 && (
                                        <Button
                                            type="link"
                                            size="small"
                                            className={s.managerRemove}
                                            onClick={() => setManagers(managers.filter((_, idx) => idx !== i))}
                                        >
                                            Remove
                                        </Button>
                                    )}
                                </div>
                                <div className={s.managerRow}>
                                    <div>
                                        <span className={s.subFieldLabel}>First name</span>
                                        <Input
                                            value={m.firstName || ''}
                                            onChange={(e) => patchAt(i, { firstName: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <span className={s.subFieldLabel}>Last name</span>
                                        <Input
                                            value={m.lastName || ''}
                                            onChange={(e) => patchAt(i, { lastName: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <span className={s.subFieldLabel}>Date of birth</span>
                                        <DatePicker
                                            style={{ width: '100%' }}
                                            format="MM/DD/YYYY"
                                            value={m.dateOfBirth ? dayjs(m.dateOfBirth) : null}
                                            disabledDate={(d) => d.isAfter(dayjs(), 'day')}
                                            onChange={(d) =>
                                                patchAt(i, { dateOfBirth: d ? d.format('YYYY-MM-DD') : undefined })
                                            }
                                        />
                                    </div>
                                    <div>
                                        <span className={s.subFieldLabel}>Social security number</span>
                                        <Input
                                            value={m.ssn || ''}
                                            placeholder="9 digits"
                                            // 回填的是掩码：获得焦点清空让人重新输入；没输入就离开则还原掩码
                                            // （掩码 + id 原样带回 = 后端保留原值）
                                            onFocus={() => {
                                                if ((m.ssn || '').includes('•')) patchAt(i, { ssn: '' });
                                            }}
                                            onBlur={() => {
                                                if (!(m.ssn || '').trim() && m.id && m.ssnLast4)
                                                    patchAt(i, { ssn: `•••-••-${m.ssnLast4}` });
                                            }}
                                            onChange={(e) =>
                                                patchAt(i, { ssn: e.target.value.replace(/[^\d-]/g, '').slice(0, 11) })
                                            }
                                        />
                                        {ssnTouched && !managerSsnValid(m.ssn, m.id) && (
                                            <div className={s.fieldError}>{SSN_ERROR}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    <Button type="link" className={s.addManagerLink} onClick={() => setManagers([...managers, {}])}>
                        + Add another person
                    </Button>
                </>
            )}
        </div>
    );
}
